import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, X, FileText, Clock, Eye, RefreshCw } from 'lucide-react'
import { contentApi, type ContentListItem } from '../../lib/api'

const formatDate = (s?: string | null) => {
  if (!s) return '-'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const MediaPill = ({ type }: { type: string }) => {
  const map: Record<string, { label: string; cls: string }> = {
    text: { label: '图文', cls: 'bg-blue-100 text-blue-700' },
    video: { label: '视频', cls: 'bg-purple-100 text-purple-700' },
    audio: { label: '音频', cls: 'bg-orange-100 text-orange-700' },
  }
  const m = map[type] || { label: type, cls: 'bg-gray-100 text-gray-700' }
  return <span className={`px-2 py-0.5 text-xs rounded-full ${m.cls}`}>{m.label}</span>
}

const StatusPill = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: '草稿', cls: 'bg-gray-100 text-gray-700' },
    pending_review: { label: '待审核', cls: 'bg-yellow-100 text-yellow-700' },
    approved: { label: '已通过', cls: 'bg-green-100 text-green-700' },
    published: { label: '已发布', cls: 'bg-emerald-100 text-emerald-700' },
    offline: { label: '已下线', cls: 'bg-zinc-100 text-zinc-700' },
    rejected: { label: '已驳回', cls: 'bg-red-100 text-red-700' },
  }
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
  return <span className={`px-2 py-0.5 text-xs rounded-full ${m.cls}`}>{m.label}</span>
}

const ContentReviewQueue = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<ContentListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [actingId, setActingId] = useState('')
  const [noteFor, setNoteFor] = useState<{ id: string; mode: 'approve' | 'reject' } | null>(null)
  const [note, setNote] = useState('')

  const load = async (p = page) => {
    setLoading(true)
    setError('')
    try {
      const res = await contentApi.adminReviewQueue({ page: p, page_size: pageSize })
      setItems(res.items)
      setTotal(res.total)
      setPage(p)
    } catch (e: any) {
      setError(e?.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(1)
  }, [])

  const startAction = (id: string, mode: 'approve' | 'reject') => {
    setNoteFor({ id, mode })
    setNote('')
  }
  const cancelAction = () => {
    setNoteFor(null)
    setNote('')
  }
  const submitAction = async () => {
    if (!noteFor) return
    if (noteFor.mode === 'reject' && !note.trim()) {
      alert('驳回必须填写备注')
      return
    }
    setActingId(noteFor.id)
    try {
      if (noteFor.mode === 'approve') {
        await contentApi.adminApproveContent(noteFor.id, note.trim() || undefined)
      } else {
        await contentApi.adminRejectContent(noteFor.id, note.trim())
      }
      cancelAction()
      await load(page)
    } catch (e: any) {
      alert(e?.message || '操作失败')
    } finally {
      setActingId('')
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-6 h-6 text-yellow-500" />
            待审核内容
            {total > 0 && <span className="text-base font-normal text-gray-500">（{total}）</span>}
          </h1>
          <p className="text-sm text-gray-500 mt-1">按提交时间排序，先提交先审核。</p>
        </div>
        <button
          onClick={() => load(page)}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">标题 / 分类</th>
              <th className="text-left px-4 py-3 font-medium w-24">类型</th>
              <th className="text-left px-4 py-3 font-medium w-28">状态</th>
              <th className="text-left px-4 py-3 font-medium w-40">提交时间</th>
              <th className="text-right px-4 py-3 font-medium w-56">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">加载中…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                <Check className="w-8 h-8 mx-auto text-green-400 mb-2" />
                当前没有待审核内容
              </td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="hover:bg-gray-50/60">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900 line-clamp-1">{it.title || '（无标题）'}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {it.category_name || '-'}
                    {it.parent_category_name ? ` / ${it.parent_category_name}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3"><MediaPill type={it.media_type} /></td>
                <td className="px-4 py-3"><StatusPill status={it.status} /></td>
                <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(it.submitted_at)}</td>
                <td className="px-4 py-3">
                  {noteFor?.id === it.id ? (
                    <div className="flex flex-col gap-2 items-end">
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={noteFor.mode === 'reject' ? '驳回原因（必填）' : '审核备注（可选）'}
                        className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded resize-none focus:outline-none focus:ring-1 focus:ring-yellow-400"
                        rows={2}
                      />
                      <div className="flex gap-1.5">
                        <button
                          onClick={cancelAction}
                          className="text-xs px-2.5 py-1 bg-white border border-gray-200 rounded hover:bg-gray-50"
                        >取消</button>
                        <button
                          onClick={submitAction}
                          disabled={actingId === it.id || (noteFor.mode === 'reject' && !note.trim())}
                          className={`text-xs px-2.5 py-1 rounded text-white flex items-center gap-1 disabled:opacity-50 ${noteFor.mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                        >
                          {noteFor.mode === 'approve' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          确认{noteFor.mode === 'approve' ? '通过' : '驳回'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-1.5">
                      <button
                        onClick={() => navigate(`/admin/content/${it.id}`)}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-gray-200 rounded text-gray-700 hover:bg-gray-50"
                      >
                        <Eye className="w-3 h-3" />预览
                      </button>
                      <button
                        onClick={() => startAction(it.id, 'approve')}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                      >
                        <Check className="w-3 h-3" />通过
                      </button>
                      <button
                        onClick={() => startAction(it.id, 'reject')}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100"
                      >
                        <X className="w-3 h-3" />驳回
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => load(page - 1)}
            className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
          >上一页</button>
          <span className="text-gray-500">{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => load(page + 1)}
            className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
          >下一页</button>
        </div>
      )}
    </div>
  )
}

export default ContentReviewQueue